import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// Actions
import { userActions } from '../Store/Actions/userActions';
import { heroActions } from '../Store/Actions/heroActions';

export const useUser = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [initialized, setInitialized] = React.useState(false);

  // Get user and hero data from Redux store
  const user = useSelector(state => state.user);
  const character = useSelector(state => state.character.character);

  // Initialize hero data when user logs in or changes
  React.useEffect(() => {
    if (user?.isAuthenticated && user?.gameData?.hero) {
      dispatch(heroActions.setHero(user.gameData.hero));
    }
  }, [user?.isAuthenticated, user?.gameData?.hero, dispatch]);

  // Initialize user state - don't auto-login from localStorage
  React.useEffect(() => {
    if (initialized) return;

    try {
      // Clear any existing authentication state to ensure fresh login
      localStorage.removeItem('user');

      // Load character data if it exists, but don't set as authenticated
      const storedCharacter = localStorage.getItem('character');

      if (storedCharacter) {
        try {
          const loadedCharacter = JSON.parse(storedCharacter);

          dispatch({
            type: 'character/update',
            payload: { character: loadedCharacter }
          });

          // heroReducer normalizes raw data through createHero — no class instance leaks
          dispatch(heroActions.setHero(loadedCharacter));
        } catch (e) {
          console.error('useUser - Error parsing character from localStorage:', e);
          localStorage.removeItem('character');
        }
      }
    } catch (error) {
      console.error('useUser - Error during initialization:', error);
    } finally {
      setInitialized(true);
    }
  }, [dispatch, initialized]);

  // Fonction de connexion. Le héros sera créé après le wizard de character-creation.
  const login = (userData) => {
    dispatch(userActions.setUser({
      id: userData.id,
      nickname: userData.nickname,
      email: userData.email,
      character: null
    }));
    localStorage.setItem('user', JSON.stringify({
      id: userData.id,
      nickname: userData.nickname,
      email: userData.email,
      character: null
    }));
    navigate('/character-creation');
  };

  // Fonction de déconnexion
  const logout = () => {
    if (process.env.NODE_ENV === 'development') {
      // Disable dev auto-login until the tab is reopened
      sessionStorage.setItem('okoyl_dev_no_auto_login', '1');
    }
    dispatch(userActions.clearUser());
    localStorage.removeItem('user');
    localStorage.removeItem('character');
    navigate('/login');
  };

  // Vérifier si l'utilisateur est connecté
  const isAuthenticated = () => {
    return !!user?.id && user.isAuthenticated;
  };

  // Vérifier si le personnage est créé
  const hasCharacter = () => {
    return !!user?.character && !!character;
  };

  return {
    user,
    character,
    isAuthenticated: isAuthenticated(),
    hasCharacter: hasCharacter(),
    login,
    logout,
    initialized
  };
};

export default useUser;
